import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Home as HomeIcon, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card } from "@/components/admin/AdminUI";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const PAGE_CONFIG = {
  accueil: {
    label: "Accueil", icon: HomeIcon,
    fields: [
      { name: "hero_badge", label: "Badge (localisation)", type: "text" },
      { name: "hero_title", label: "Titre — début", type: "text" },
      { name: "hero_highlight", label: "Titre — partie en vert", type: "text" },
      { name: "hero_title_end", label: "Titre — fin", type: "text" },
      { name: "hero_subtitle", label: "Sous-titre du hero", type: "textarea" },
      { name: "hero_image", label: "Image du hero (URL)", type: "text" },
      { name: "stat1_value", label: "Statistique 1 — valeur", type: "text" },
      { name: "stat1_label", label: "Statistique 1 — libellé", type: "text" },
      { name: "stat2_value", label: "Statistique 2 — valeur", type: "text" },
      { name: "stat2_label", label: "Statistique 2 — libellé", type: "text" },
      { name: "stat3_value", label: "Statistique 3 — valeur", type: "text" },
      { name: "stat3_label", label: "Statistique 3 — libellé", type: "text" },
      { name: "cta_title", label: "CTA final — titre", type: "text" },
      { name: "cta_subtitle", label: "CTA final — sous-titre", type: "textarea" },
    ],
  },
  "a-propos": {
    label: "À propos", icon: Info,
    fields: [
      { name: "intro", label: "Introduction", type: "textarea" },
      { name: "mission", label: "Notre mission", type: "textarea" },
      { name: "vision", label: "Notre vision", type: "textarea" },
      { name: "group_text", label: "VISION LOGICIELS & PERFORM & CO", type: "textarea" },
    ],
  },
};

function PageEditor({ pageKey }) {
  const cfg = PAGE_CONFIG[pageKey];
  const [sections, setSections] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/pages/${pageKey}`).then(({ data }) => setSections(data.sections || {})).catch(() => {}); }, [pageKey]);
  const set = (k) => (e) => setSections({ ...sections, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try { await api.put(`/admin/pages/${pageKey}`, { sections, title: cfg.label }); toast.success("Page enregistrée"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-6">
      <div className="flex justify-end mb-4">
        <button onClick={save} disabled={saving} data-testid={`page-save-${pageKey}`} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#3B82F6] text-white text-sm font-semibold disabled:opacity-60"><Save className="w-4 h-4" /> Enregistrer</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {cfg.fields.map((f) => (
          <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
            <label className="text-sm text-slate-300">{f.label}</label>
            {f.type === "textarea"
              ? <Textarea value={sections[f.name] || ""} onChange={set(f.name)} rows={3} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-white" />
              : <Input value={sections[f.name] || ""} onChange={set(f.name)} className="mt-1.5 bg-[#0B0F17] border-[#1E293B] text-white" />}
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function PagesAdmin() {
  return (
    <div>
      <PageHeader title="Pages du site" subtitle="Modifiez les textes et images des pages sans toucher au code." />
      <Tabs defaultValue="accueil">
        <TabsList className="dark bg-[#111827] border border-[#1E293B]">
          {Object.entries(PAGE_CONFIG).map(([k, c]) => <TabsTrigger key={k} value={k} data-testid={`pages-tab-${k}`}>{c.label}</TabsTrigger>)}
        </TabsList>
        {Object.keys(PAGE_CONFIG).map((k) => <TabsContent key={k} value={k} className="mt-4"><PageEditor pageKey={k} /></TabsContent>)}
      </Tabs>
    </div>
  );
}
